import React from 'react'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ISkeletonWrapperProps {
  /**
   * When `true`, renders the `skeleton` slot instead of `children`.
   * Toggling this value triggers a seamless swap with no layout shift,
   * provided the `skeleton` dimensions match the real content.
   */
  isLoading: boolean

  /**
   * The skeleton element to display while loading.
   * Use one of the `Skeleton.*` variants or compose `Skeleton.Base` blocks.
   *
   * @example
   * skeleton={<Skeleton.Card count={3} />}
   */
  skeleton: React.ReactNode

  /**
   * The real content shown once loading is complete.
   */
  children: React.ReactNode
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Generic loading-state wrapper.
 *
 * Swap between a skeleton placeholder and real content based on `isLoading`.
 * Works with any async data-fetching state (React Query, manual fetch, etc.)
 * and with route-transition loading via `useRouteTransition`.
 *
 * @example — async data
 * const { data, isLoading } = useMyQuery()
 *
 * <SkeletonWrapper isLoading={isLoading} skeleton={<Skeleton.Table rows={6} />}>
 *   <MyDataTable data={data} />
 * </SkeletonWrapper>
 *
 * @example — route transition (in Layout)
 * const { isTransitioning } = useRouteTransition()
 *
 * <SkeletonWrapper isLoading={isTransitioning} skeleton={<Skeleton.PageSection />}>
 *   <Outlet />
 * </SkeletonWrapper>
 */
export const SkeletonWrapper = ({
  isLoading,
  skeleton,
  children,
}: ISkeletonWrapperProps): JSX.Element => {
  if (isLoading) {
    return <>{skeleton}</>
  }

  return <>{children}</>
}
